import { useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '@/lib/picasso-firebase';

interface UseLogoutReturn {
  logout: () => Promise<void>;
  isLoggingOut: boolean;
  logoutError: string | null;
  clearLogoutError: () => void;
}

export const useLogout = (): UseLogoutReturn => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  const logout = async (): Promise<void> => {
    setIsLoggingOut(true);
    setLogoutError(null);

    try {
      // Step 1: Call backend logout endpoint if it exists
      try {
        const baseUrl = import.meta.env.VITE_BASE_URL;
        await fetch(`${baseUrl}/picasso/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
          }
        });
      } catch (error) {
        // Backend logout failed, but continue with client-side cleanup
        console.error('Backend logout failed:', error);
        
      }

      // Step 2: Clear browser cache
      if ('caches' in globalThis) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }

      // Step 3: Sign out from Firebase
      await signOut(auth);
      
      // Step 4: Clear all local storage and session data
      localStorage.removeItem('userToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('user_id');
      localStorage.removeItem('userEmail');
      sessionStorage.clear();
      
      // Step 5: Redirect to login page
      globalThis.location.href = '/login';
      
    } catch (error: any) {
      setLogoutError(error.message || 'Failed to logout. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const clearLogoutError = () => {
    setLogoutError(null);
  };

  return {
    logout,
    isLoggingOut,
    logoutError,
    clearLogoutError
  };
};